import { Component, OnInit } from "@angular/core";
import {
    AddressDetailsAttributes,
    NullableRegisteredUserDetails,
    SidebarSections,
    UserDetailStatus,
} from "@models/user.model";
import { finalize } from "rxjs";
import { appConfig } from "~/src/environments/app.config";
import { environment } from "~/src/environments/environment";
import { UserService } from "../../services/user/user.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { ProductService } from "../../services/product/product.service";
import { S3UploadService } from "../../services/s3-upoad/s3-upload.service";
import { Router } from "@angular/router";
import { AuthService } from '../../services/auth/auth.service';

@Component({
    selector: "app-overview",
    templateUrl: "./overview.component.html",
    styleUrl: "./overview.component.scss",
    standalone: false,
})
export class OverviewComponent implements OnInit {
    public userDetails!: NullableRegisteredUserDetails;
    public address!: AddressDetailsAttributes;
    public profileImage!: string;
    public image!: string;
    public isLoading: boolean = true;
    public sidebarSections = SidebarSections;
    public profileLabel = UserDetailStatus;

    constructor(
        private userService: UserService,
        private _snackBar: MatSnackBar,
        private productService: ProductService,
        private s3UploadService: S3UploadService,
        private translate: TranslateService,
        private router: Router,
        private authService: AuthService,
    ) {}

    ngOnInit(): void {
        this.getUserDetails();
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private getUserDetails(): void {
        this.isLoading = true;
        this.userService
            .getUserDetails()
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe((response: NullableRegisteredUserDetails) => {
                this.userDetails = response;
                this.profileImage = response?.user.profileImage!;
            });
    }

    public async uploadFile(event: Event, fileType: string) {
        const maxImageSize = +appConfig.fileSize.profileImageSize;
        const input = event.target as HTMLInputElement;
        const partSize = 5 * 1024 * 1024;
        if (input.files && fileType === "image") {
            const file = input.files[0];
            if (input.files[0].size < maxImageSize) {
                const parts = Math.ceil(file.size / partSize);
                const partNumbers = Array.from({ length: parts }, (_, i) => i + 1);
                let filePathWithName;
                filePathWithName = `${environment.name}/profileImage/${file.name}`;
                const url = await this.s3UploadService.uploadFile(filePathWithName, partNumbers, file);
                this.userService.uploadProfileImage({ imageUrl: url }).subscribe((uploadResponse) => {
                    this.getUserDetails();
                });
            } else {
                alert(this.getTranslation("profile.invalidMessage.maximumFileSize"));
                return;
            }
        } else {
            const url = this.userDetails?.user.profileImage;
            if (url !== undefined) {
                this.productService.deleteFile(url).subscribe((removeImageFromS3Response) => {
                    if (removeImageFromS3Response) {
                        this.userService.uploadProfileImage({ imageUrl: fileType }).subscribe((response) => {
                            if (response) {
                                this.getUserDetails();
                            }
                        });
                    }
                });
            }
        }
    }

    public receiveAddressData(address: AddressDetailsAttributes): void {
        this.address = address;
    }

    public onUpdate(): void {
        const body = this.address;
        this.userService.updateAddress(body).subscribe({
            next: (response) => {
                this.getUserDetails();
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
            },
            error: (error) => {
                throw new Error(error.message);
            },
        });
    }

    private openSnackBar(message: string, action: string): void {
        this._snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    public goBack(): void {
        this.router.navigate(["/home"]);
    }

    public clickAddUserDetails(): void {
        this.router.navigate(["/userDetails"]);
    }

    public logOut(): void {
        this.authService.logout();
    }
}
